import { useAuth, useSessionList } from '@clerk/react';
import type { SessionResource } from '@clerk/react/types';
import SplashLoader from '@components/atoms/SplashLoader';
import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useClerkSessionFinalizer } from 'src/pages/auth/hooks/useClerkSessionFinalizer';
import type { ClerkSessionFinalization } from 'src/pages/auth/hooks/useClerkSessionFinalizer';
import { isClerkAuthEnabled } from 'src/utils/clerkAuth';

import paths from './paths';

const PendingSessionResume = ({ session }: { session: SessionResource }) => {
  const { validateActiveSession } = useClerkSessionFinalizer();
  const [result, setResult] = useState<ClerkSessionFinalization>('unavailable');
  const operation = useRef<Promise<ClerkSessionFinalization> | null>(null);
  const [searchParams] = useSearchParams();
  const hasPurchaseSelection = ['planKey', 'interval', 'market'].every((key) => searchParams.get(key)?.trim());

  useEffect(() => {
    let mounted = true;
    operation.current ??= validateActiveSession(session);
    void operation.current.then(
      (status) => {
        if (mounted) setResult(status === 'unavailable' ? 'contact-support' : status);
      },
      () => {
        if (mounted) setResult('contact-support');
      },
    );
    return () => {
      mounted = false;
    };
  }, [session, validateActiveSession]);

  if (result === 'unavailable') return <SplashLoader />;
  // Zero-org pending session with purchase selection: contact-support still reaches purchase (Policy enforces EXISTING/AMBIGUOUS).
  if (result === 'no-organization' && !hasPurchaseSelection) return <Navigate to={paths.authTrial} replace />;
  if (result !== 'ready')
    return <Navigate to={hasPurchaseSelection ? `${paths.purchase}?${searchParams}` : '/auth/no-account'} replace />;
  return <Navigate to={hasPurchaseSelection ? `${paths.purchase}?${searchParams}` : paths.app.dashboard} replace />;
};

const ClerkPublicGate: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: sessionsLoaded, sessions } = useSessionList();
  const recoveringSession = useRef<SessionResource | null>(null);
  const pendingSessions = sessions?.filter((session) => session.status === 'pending') ?? [];
  if (!isSignedIn && !recoveringSession.current && pendingSessions.length === 1) {
    if (pendingSessions[0].currentTask?.key === 'choose-organization') recoveringSession.current = pendingSessions[0];
  }
  const [searchParams] = useSearchParams();
  const hasPurchaseSelection = ['planKey', 'interval', 'market'].every((key) => searchParams.get(key)?.trim());

  if (!isLoaded || !sessionsLoaded) return <SplashLoader />;
  if (recoveringSession.current) return <PendingSessionResume session={recoveringSession.current} />;
  if (!isSignedIn && pendingSessions.length > 0)
    return <Navigate to={hasPurchaseSelection ? `${paths.purchase}?${searchParams}` : '/auth/no-account'} replace />;
  if (isSignedIn && !hasPurchaseSelection) return <Navigate to={paths.app.dashboard} />;

  return <Outlet />;
};

const OutsetaPublicGate: React.FC = () => {
  const token = localStorage.getItem('Outseta.nocode.accessToken');

  // An already-authenticated visit to a public route (e.g. /auth) is one of the 5+
  // hardcoded post-login-landing sites — all of them land on Dashboard, the overview of
  // "Gestisci la tua attività" (now the first sidebar entry). Spartacus tokens still end up
  // on spartacus.suggestions regardless: the downstream PropertyGuardRoute on the /app tree
  // redirects by JWT aud, independent of whichever path lands here first.
  if (token) {
    return <Navigate to={paths.app.dashboard} />;
  }

  return <Outlet />;
};

export const PublicRoute: React.FC = () => (isClerkAuthEnabled() ? <ClerkPublicGate /> : <OutsetaPublicGate />);
